import axios from "axios"; // your existing axios/api helper
import _ from "lodash";
import websocketService from "@/services/websocketService";

const getDefaultState = () => {
    return {
        Rooms: [],
        ActiveRoomID: null,
        MessagesByRoom: {},   // { [roomID]: [...messages] }
        HasMoreMessagesByRoom: {},  // { [roomID]: boolean }
        ConnectionStatus: 'disconnected',  // 'connected' | 'disconnected' | 'error' | 'failed'
        MemberReadPositionsByRoom: {},   // { [roomID]: { [userID]: lastReadAt } }
        LoadingRooms: false,
        LoadingMessages: false,
        LoadingMoreMessages: false,
        LoadingUsers: false,
        LoadingUsersPictures: false,
        LinkedUsersPerJourney: {},
        UsersLinkedToCurrentUser: [],
        WebsocketUrl: null,
        AttachmentUrlCache: {},  // { [fileID]: { url, expiresAt } }
        EditingMessage: null,    // { ID, RoomID, Content } — message being edited
        CompanionStreamingByRoom: {},
        StreamingMessageByRoom: {},
    }
};

const state = getDefaultState();

const getters = {

    websocketUrl: (state) => state.WebsocketUrl,

    rooms: (state) => state.Rooms,

    activeRoom: (state) => state.Rooms.find((r) => r.RoomID === state.ActiveRoomID) ?? null,

    activeMessages: (state) => {
        const roomID = state.ActiveRoomID;
        const messages = state.MessagesByRoom[roomID] ?? [];
        const streaming = state.StreamingMessageByRoom[roomID];
        if (streaming) {
            return [...messages, streaming];
        }
        return messages;
    },

    companionRooms: (state) => state.Rooms.filter((r) => r.Type === 'companion'),

    isCompanionStreaming: (state) => (roomID) => !!state.CompanionStreamingByRoom[roomID],

    activeRoomIsCompanion: (state, getters) => getters.activeRoom?.Type === 'companion',

    connectionStatus: (state) => state.ConnectionStatus,

    isConnected: (state) => state.ConnectionStatus === 'connected',

    loadingRooms: (state) => state.LoadingRooms,

    loadingMessages: (state) => state.LoadingMessages,

    loadingMoreMessages: (state) => state.LoadingMoreMessages,

    hasMoreMessages: (state) => state.HasMoreMessagesByRoom[state.ActiveRoomID] ?? false,

    loadingUsers: (state) => state.LoadingUsers,

    loadingUsersPictures: (state) => state.LoadingUsersPictures,

    linkedUsersPerJourney: (state) => state.LinkedUsersPerJourney,

    usersLinkedToCurrentUser: (state) => state.UsersLinkedToCurrentUser,

    totalUnread: (state) => state.Rooms.reduce((sum, room) => sum + (room.UnreadCount ?? 0), 0),

    memberReadPositionsByRoom: (state) => state.MemberReadPositionsByRoom,

    editingMessage: (state) => state.EditingMessage,

};

const mutations = {

    SET_ROOMS(state, rooms) {
        state.Rooms = rooms;
    },

    SET_LINKED_USERS(state, data) {
        state.LinkedUsersPerJourney = data.linkedUsersPerJourney;
        state.UsersLinkedToCurrentUser = data.usersLinkedToCurrentUser;
        // if (state.UsersLinkedToCurrentUser.length > 0) {
        //     for (let responseUser of data.usersLinkedToCurrentUser) {
        //         let foundStateUser = state.UsersLinkedToCurrentUser.find((stateUser) => stateUser.sub === responseUser.sub);
        //         let backupProfilePictureURL = foundStateUser.profilePictureURL ? foundStateUser.profilePictureURL : null;
        //         if (foundStateUser) {
        //             foundStateUser.family_name = responseUser.family_name;
        //             foundStateUser.given_name = responseUser.given_name;
        //             foundStateUser.picture = responseUser.picture;
        //             foundStateUser.sub = responseUser.sub;
        //             if (backupProfilePictureURL) {
        //                 foundStateUser.profilePictureURL = backupProfilePictureURL;
        //             }
        //         }
        //         else {
        //             state.UsersLinkedToCurrentUser.push(responseUser);
        //         }
        //     }
        // }
        // else {
        //     state.UsersLinkedToCurrentUser = data.usersLinkedToCurrentUser;
        // }
    },

    SET_ROOM_NAME(state, data) {
        let updatedRoom = state.Rooms.find((room) => room.RoomID === data.RoomID);
        if (updatedRoom) {
            updatedRoom.Name = data.RoomName;
        }
    },

    SET_ROOM_PICTURE(state, data) {
        let updatedRoom = state.Rooms.find((room) => room.RoomID === data.RoomID);
        if (updatedRoom) {
            updatedRoom.Picture = data.RoomPicture;
        }
    },

    SET_ROOM_MEMBERS(state, data) {
        let updatedRoom = state.Rooms.find((room) => room.RoomID === data.RoomID);
        if (updatedRoom) {
            updatedRoom.Members = data.UpdatedMembers;
            updatedRoom.JoinedAt = data.UpdatedJoinedAt;
        }
    },

    SET_ROOM_ADMINS(state, data) {
        let updatedRoom = state.Rooms.find((room) => room.RoomID === data.RoomID);
        if (updatedRoom) {
            updatedRoom.Admins = data.Admins;
        }
    },

    PATCH_ROOM(state, patch) {
        const room = state.Rooms.find((r) => r.RoomID === patch.RoomID);
        if (!room) return;
        if (patch.Name !== undefined) room.Name = patch.Name;
        if (patch.Picture !== undefined) room.Picture = patch.Picture;
        if (patch.Members !== undefined) room.Members = patch.Members;
        if (patch.JoinedAt !== undefined) room.JoinedAt = patch.JoinedAt;
        if (patch.Admins !== undefined) room.Admins = patch.Admins;
    },

    ADD_ROOM(state, room) {
        const exists = state.Rooms.some((r) => r.RoomID === room.RoomID);
        if (!exists) {
            state.Rooms.unshift(room);
        }
    },

    SET_ACTIVE_ROOM(state, roomID) {
        state.ActiveRoomID = roomID;
    },

    REMOVE_ROOM(state, roomID) {
        state.Rooms = state.Rooms.filter((r) => r.RoomID !== roomID);
        delete state.MessagesByRoom[roomID];
        delete state.HasMoreMessagesByRoom[roomID];
        delete state.MemberReadPositionsByRoom[roomID];
        delete state.StreamingMessageByRoom[roomID];
        delete state.CompanionStreamingByRoom[roomID];
    },

    SET_MESSAGES(state, { roomID, messages }) {
        state.MessagesByRoom[roomID] = messages;
    },

    PREPEND_MESSAGES(state, { roomID, messages }) {
        const existing = state.MessagesByRoom[roomID] ?? [];
        const existingIDs = new Set(existing.map((m) => m.ID));
        const fresh = messages.filter((m) => !existingIDs.has(m.ID));
        state.MessagesByRoom[roomID] = [...fresh, ...existing];
    },

    SET_HAS_MORE_MESSAGES(state, { roomID, hasMore }) {
        state.HasMoreMessagesByRoom[roomID] = hasMore;
    },

    SET_LOADING_MORE_MESSAGES(state, val) {
        state.LoadingMoreMessages = val;
    },

    ADD_OPTIMISTIC_MESSAGE(state, { message, currentUserID }) {
        if (!state.MessagesByRoom[message.RoomID]) {
            state.MessagesByRoom[message.RoomID] = [];
        }
        const roomMessages = state.MessagesByRoom[message.RoomID];
        const exists = roomMessages.some((m) => m.ID === message.ID);
        if (!exists) roomMessages.push(message);
        // Increment unread count only if not the active room and not sent by this user
        if (state.ActiveRoomID !== message.RoomID && message.SenderID !== currentUserID) {
            const room = state.Rooms.find((r) => r.RoomID === message.RoomID);
            if (room) room.UnreadCount = (room.UnreadCount ?? 0) + 1;
        }
    },


    // Replaces an optimistic message (same sender + content/attachment) with the confirmed server message,
    // or just adds it if no matching optimistic exists.
    REPLACE_OR_ADD_MESSAGE(state, { message, currentUserID }) {
        const roomMessages = state.MessagesByRoom[message.RoomID];
        if (!roomMessages) {
            // Room not opened yet — don't initialize with a partial array so openRoom still calls fetchMessages.
            if (state.ActiveRoomID !== message.RoomID && message.SenderID !== currentUserID) {
                const room = state.Rooms.find((r) => r.RoomID === message.RoomID);
                if (room) room.UnreadCount = (room.UnreadCount ?? 0) + 1;
            }
            return;
        }
        const optimisticIdx = roomMessages.findIndex((m) => {
            if (!m.isOptimistic || m.SenderID !== message.SenderID) return false;
            if (message.Attachments?.length) return m.Attachments?.[0]?.FileID === message.Attachments[0].FileID;
            return m.Content === message.Content && !m.Attachment;
        });
        if (optimisticIdx !== -1) {
            roomMessages.splice(optimisticIdx, 1, message);
        } else {
            const exists = roomMessages.some((m) => m.ID === message.ID);
            if (!exists) roomMessages.push(message);
        }
        if (state.ActiveRoomID !== message.RoomID && message.SenderID !== currentUserID) {
            const room = state.Rooms.find((r) => r.RoomID === message.RoomID);
            if (room) room.UnreadCount = (room.UnreadCount ?? 0) + 1;
        }
    },

    START_STREAMING_MESSAGE(state, { roomID, streamMessageID }) {
        state.CompanionStreamingByRoom[roomID] = true;
        const existing = state.StreamingMessageByRoom[roomID];
        if (existing) {
            existing.ID = streamMessageID;
            return;
        }
        state.StreamingMessageByRoom[roomID] = {
            ID: streamMessageID,
            RoomID: roomID,
            SenderID: 'commovis-companion',
            Content: '',
            Timestamp: new Date().toISOString(),
            isStreaming: true,
        };
    },

    APPEND_STREAMING_DELTA(state, { roomID, content }) {
        const msg = state.StreamingMessageByRoom[roomID];
        if (msg) {
            msg.Content = content;
        }
    },

    FINISH_STREAMING_MESSAGE(state, { roomID }) {
        delete state.CompanionStreamingByRoom[roomID];
        delete state.StreamingMessageByRoom[roomID];
    },

    MARK_ROOM_READ(state, roomID) {
        const room = state.Rooms.find((r) => r.RoomID === roomID);
        if (room) room.UnreadCount = 0;
    },

    SET_MEMBER_READ_POSITIONS(state, { roomID, positions }) {
        state.MemberReadPositionsByRoom[roomID] = positions;
    },

    UPDATE_MEMBER_READ_POSITION(state, { roomID, userID, readAt }) {
        if (!state.MemberReadPositionsByRoom[roomID]) {
            state.MemberReadPositionsByRoom[roomID] = {};
        }
        state.MemberReadPositionsByRoom[roomID][userID] = readAt;
    },

    UPDATE_ROOM_LAST_MESSAGE_PREVIEW(state, { roomID, preview }) {
        const room = state.Rooms.find((r) => r.RoomID === roomID);
        if (room) room.LastMessagePreview = preview;
    },

    UPDATE_ROOM_LAST_MESSAGE(state, message) {
        const room = state.Rooms.find((r) => r.RoomID === message.RoomID);
        if (room) {
            room.LastMessageAt = message.Timestamp;
            const previewText = message.Content || (message.Attachments?.length ? `📎 ${message.Attachments[0].FileName}` : '');
            room.LastMessagePreview = previewText.substring(0, 100);
            state.Rooms.sort((a, b) => {
                if (a.IsPinned && !b.IsPinned) return -1;
                if (!a.IsPinned && b.IsPinned) return 1;
                return new Date(b.LastMessageAt) - new Date(a.LastMessageAt);
            });
        }
    },

    SET_ROOM_PINNED(state, { RoomID, IsPinned }) {
        const room = state.Rooms.find((r) => r.RoomID === RoomID);
        if (room) {
            room.IsPinned = IsPinned;
            state.Rooms.sort((a, b) => {
                if (a.IsPinned && !b.IsPinned) return -1;
                if (!a.IsPinned && b.IsPinned) return 1;
                if (!a.LastMessageAt && !b.LastMessageAt) return 0;
                if (!a.LastMessageAt) return 1;
                if (!b.LastMessageAt) return -1;
                return new Date(b.LastMessageAt) - new Date(a.LastMessageAt);
            });
        }
    },

    ADD_REACTION(state, { roomID, messageID, emoji, userID }) {
        const messages = state.MessagesByRoom[roomID];
        if (!messages) return;
        const msg = messages.find((m) => m.ID === messageID);
        if (!msg) return;
        if (!msg.Reactions) msg.Reactions = {};
        if (!msg.Reactions[emoji]) msg.Reactions[emoji] = [];
        if (!msg.Reactions[emoji].includes(userID)) {
            msg.Reactions[emoji].push(userID);
        }
    },

    REMOVE_REACTION(state, { roomID, messageID, emoji, userID }) {
        const messages = state.MessagesByRoom[roomID];
        if (!messages) return;
        const msg = messages.find((m) => m.ID === messageID);
        if (!msg || !msg.Reactions?.[emoji]) return;
        msg.Reactions[emoji] = msg.Reactions[emoji].filter((id) => id !== userID);
        if (msg.Reactions[emoji].length === 0) {
            delete msg.Reactions[emoji];
        }
    },

    UPDATE_MESSAGE(state, { roomID, messageID, content, editedAt }) {
        const messages = state.MessagesByRoom[roomID];
        if (!messages) return;
        const msg = messages.find((m) => m.ID === messageID);
        if (!msg) return;
        msg.Content = content;
        msg.EditedAt = editedAt;
    },

    REMOVE_MESSAGE(state, { roomID, messageID }) {
        if (!state.MessagesByRoom[roomID]) return;
        state.MessagesByRoom[roomID] = state.MessagesByRoom[roomID].filter((m) => m.ID !== messageID);
    },

    UPDATE_ROOM_ON_MESSAGE_DELETED(state, { roomID, deletedMessageTimestamp, newLastMessageAt, newLastMessagePreview, currentUserID }) {
        const room = state.Rooms.find((r) => r.RoomID === roomID);
        if (!room) return;

        if (newLastMessageAt !== undefined) {
            room.LastMessageAt = newLastMessageAt;
            room.LastMessagePreview = newLastMessagePreview ?? '';
            state.Rooms.sort((a, b) => {
                if (a.IsPinned && !b.IsPinned) return -1;
                if (!a.IsPinned && b.IsPinned) return 1;
                if (!a.LastMessageAt && !b.LastMessageAt) return 0;
                if (!a.LastMessageAt) return 1;
                if (!b.LastMessageAt) return -1;
                return new Date(b.LastMessageAt) - new Date(a.LastMessageAt);
            });
        }

        const userReadAt = state.MemberReadPositionsByRoom[roomID]?.[currentUserID];
        const wasUnread = !userReadAt || userReadAt < deletedMessageTimestamp;
        if (wasUnread && room.UnreadCount > 0) {
            room.UnreadCount = Math.max(0, room.UnreadCount - 1);
        }
    },

    SET_CONNECTION_STATUS(state, status) {
        state.ConnectionStatus = status;
    },

    SET_JOINED_ROOM(state, roomID) {
        // Can use this to track confirmed room subscriptions if needed
        // console.log('Confirmed joined room:', roomID);
    },

    SET_LOADING_ROOMS(state, val) {
        state.LoadingRooms = val;
    },

    SET_LOADING_MESSAGES(state, val) {
        state.LoadingMessages = val;
    },

    SET_LOADING_USERS(state, val) {
        state.LoadingUsers = val;
    },

    SET_LOADING_USERS_PICTURES(state, val) {
        state.LoadingUsersPictures = val;
    },

    SET_WEBSOCKET_URL(state, url) {
        state.WebsocketUrl = url;
    },

    CACHE_ATTACHMENT_URL(state, { fileID, url }) {
        state.AttachmentUrlCache[fileID] = {
            url,
            expiresAt: Date.now() + 3500 * 1000, // 3500s — slightly under the 3600s presigned URL validity
        };
    },

    SET_EDITING_MESSAGE(state, message) {
        state.EditingMessage = message;
    },

    RESET_STATE(state) {
        Object.assign(state, getDefaultState())
    },

};

const actions = {

    async fetchConfig({ commit, dispatch, state, rootState }) {

        try {
            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            const response = await axios.get('/v2/config/websocket-url', config);
            commit('SET_WEBSOCKET_URL', response.data.websocketUrl);
        }
        catch (err) {
            // console.error('Failed to fetch WebSocket config:', err);
            dispatch('notifications/addNotification', {
                type: 'error',
                message: 'Chat is unavailable. Please refresh the page.',
            }, { root: true });
        }

    },

    async fetchRooms({ commit, dispatch, state, rootState }) {

        commit('SET_LOADING_ROOMS', true);

        try {

            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            const response = await axios.get(`/v2/chat/rooms`, config);

            commit('SET_ROOMS', response.data);
        } catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. Your conversations couldn't be loaded.",
            }, { root: true });
        } finally {
            commit('SET_LOADING_ROOMS', false);
        }
    },

    async fetchMessages({ commit, dispatch, state, rootState }, { roomID, silent = false } = {}) {

        if (!silent) {
            commit('SET_LOADING_MESSAGES', true);
        }

        try {
            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            const response = await axios.post(`/v2/chat/rooms/${roomID}/messages`, {}, config);

            commit('SET_MESSAGES', {
                roomID,
                messages: response.data.messages,
            });

            if (response.data.memberReadPositions) {
                commit('SET_MEMBER_READ_POSITIONS', {
                    roomID,
                    positions: response.data.memberReadPositions,
                });
            }

            // assume that if 50 messages were fetched, there are still more messages to be fetched
            commit('SET_HAS_MORE_MESSAGES', {
                roomID,
                hasMore: response.data.messages.length === 50,
            });
        } catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. Messages couldn't be loaded.",
            }, { root: true });
        } finally {
            if (!silent) {
                commit('SET_LOADING_MESSAGES', false);
            }
        }
    },

    async fetchMoreMessages({ commit, state, rootState }, roomID) {
        const messages = state.MessagesByRoom[roomID] ?? [];
        if (!messages.length) return;

        commit('SET_LOADING_MORE_MESSAGES', true);

        try {
            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer
                }
            };

            const response = await axios.post(
                `/v2/chat/rooms/${roomID}/messages`,
                { before: messages[0].Timestamp },
                config
            );

            commit('PREPEND_MESSAGES', { roomID, messages: response.data.messages });

            // assume that if 50 messages were fetched, there are still more messages to be fetched
            commit('SET_HAS_MORE_MESSAGES', {
                roomID,
                hasMore: response.data.messages.length === 50,
            });
        } catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. Older messages couldn't be loaded.",
            }, { root: true });
        } finally {
            commit('SET_LOADING_MORE_MESSAGES', false);
        }
    },

    async createRoom({ commit, dispatch, state, rootState }, payload) {

        try {

            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            const payloadAPI = {
                Type: payload.Type,
                MemberIDs: payload.MemberIDs,
                Name: payload.Name,
                JourneyID: payload.JourneyID,
            };

            if (!Array.isArray(payload.MemberIDs)) {
                payloadAPI.MemberIDs = [payload.MemberIDs];
            }

            const response = await axios.post('/v2/chat/rooms/create-room', payloadAPI, config);
            const { RoomID, CurrentUserNewRoom, Existing } = response.data;

            if (!Existing && CurrentUserNewRoom) {
                commit('ADD_ROOM', CurrentUserNewRoom);
            }
            else if (Existing) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: "A direct chat with this user already exists."
                }, {root: true});
            }

            return {
                RoomID: RoomID,
                Existing: Existing,
            };
        }
        catch (err){
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. The conversation couldn't be created.",
            }, { root: true });
        }

    },

    async createCompanionSession({ dispatch, rootState }, { name } = {}) {
        const sessionName = name || `Conversation ${new Date().toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })}`;

        const result = await dispatch('createRoom', {
            Type: 'companion',
            MemberIDs: [],
            Name: sessionName,
            JourneyID: null,
        });

        if (result?.RoomID && !result.Existing) {
            await dispatch('openRoom', result.RoomID);
        }

        return result;
    },

    async togglePinRoom({ commit, dispatch, rootState }, roomID) {

        try {
            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            const payloadAPI = {
                RoomID: roomID
            };

            const response = await axios.post('/v2/chat/rooms/toggle-pin-room', payloadAPI, config);

            commit('SET_ROOM_PINNED', response.data);

        } catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: 'Something went wrong, conversation pin is not changed'
            }, {root: true});
        }
    },

    async markRoomAsRead({ commit, dispatch, state, rootState, rootGetters }, roomID) {

        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        const config = {
            headers: {
                Authorization: rootState.auth.bearer,
            },
        };

        try {
            let payloadAPI = {
                RoomID: roomID,
            }
            await axios.post(`/v2/chat/rooms/mark-room-as-read`, payloadAPI, config);
            commit('MARK_ROOM_READ', roomID);
            const currentUserID = rootGetters['auth/getCurrentUserID'];
            if (currentUserID) {
                commit('UPDATE_MEMBER_READ_POSITION', {
                    roomID,
                    userID: currentUserID,
                    readAt: new Date().toISOString(),
                });
            }
        }
        catch (error) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. The conversation couldn't be marked as read.",
            }, { root: true });
        }

    },

    async openRoom({ commit, dispatch, state, rootState }, roomID) {

        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        const config = {
            headers: {
                Authorization: rootState.auth.bearer,
            },
        };

        commit('SET_ACTIVE_ROOM', roomID);
        commit('SET_EDITING_MESSAGE', null);

        if (state.MessagesByRoom[roomID]) {
            // Show cached messages immediately, refresh silently in background
            dispatch('fetchMessages', { roomID, silent: true });
        }
        else {
            try {
                await dispatch('fetchMessages', { roomID });
            }
            catch (error) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: "Something went wrong, couldn't fetch conversation messages"
                }, {root: true});
            }
        }

        dispatch('markRoomAsRead', roomID);

        // Tell the backend we're listening to this room
        // const wsService = (await import('@/services/websocketService')).default;
        websocketService.joinRoom(roomID);
    },

    closeRoom({ commit }) {
        commit('SET_ACTIVE_ROOM', null);
    },

    async fetchUsersLinkedToCurrentUser({ commit, dispatch, state, rootState }) {

        try {

            commit('SET_LOADING_USERS', true);
            commit('SET_LOADING_USERS_PICTURES', true);

            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            if (state.UsersLinkedToCurrentUser.length > 0 && !_.isEmpty(state.LinkedUsersPerJourney)) {
                commit('SET_LOADING_USERS', false);
            }

            const payloadAPI = {};

            const response = await axios.post('/v2/users/list-users-linked-to-current-user', payloadAPI, config);

            // for (let user of response.data.usersLinkedToCurrentUser) {
            //     if (!user.picture) continue;
            //     user.profilePictureURL = await dispatch('file/getFileURL', {
            //         fileName: user.picture,
            //         folderName: `profile`
            //     }, {root: true});
            // }

            commit('SET_LINKED_USERS', response.data);

            commit('SET_LOADING_USERS_PICTURES', false);

            commit('SET_LOADING_USERS', false);

        }
        catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. Users linked to you couldn't be fetched."
            }, {root: true});
            // commit('SET_LOADING_USERS', false);
            // commit('SET_LOADING_USERS_PICTURES', false);
        }
    },

    async addRoomMembers({ dispatch, commit, state, rootState }, payload) {

        try {
            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            let payloadAPI = {
                RoomID: payload.RoomID,
                MemberIDs: payload.MemberIDs,
            }

            let response = await axios.post(`/v2/chat/rooms/add-room-members`, payloadAPI, config);

            // commit('SET_ROOM_MEMBERS', response.data);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Users successfully added to the group chat'
            }, {root: true});

            // await dispatch('fetchRooms');
        }
        catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. Users weren't added to the group chat."
            }, {root: true});
        }

    },

    async removeRoomMember({ commit, dispatch, state, rootState }, payload) {

        try {

            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            let payloadAPI = {
                RoomID: payload.RoomID,
                MemberID: payload.MemberID,
            }

            let response = await axios.post(`/v2/chat/rooms/remove-room-member`, payloadAPI, config);

            // commit('SET_ROOM_MEMBERS', response.data);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'User removed successfully from group chat '
            }, {root: true});

            // await dispatch('fetchRooms');

        }
        catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. User wasn't removed from the group chat."
            }, {root: true});
        }

    },

    async makeRoomAdmin({ commit, dispatch, state, rootState }, payload) {

        try {

            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            let payloadAPI = {
                RoomID: payload.RoomID,
                MemberID: payload.MemberID,
            };

            const response = await axios.post(`/v2/chat/rooms/make-room-admin`, payloadAPI, config);

            // commit('SET_ROOM_ADMINS', { RoomID: payload.RoomID, Admins: response.data.Admins });

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'User successfully made admin'
            }, {root: true});

        }
        catch (err) {

            let msg = "Something went wrong. User wasn't made admin.";

            if (err?.response?.data?.message) {
                msg = err?.response?.data?.message;
            }
            dispatch('notifications/addNotification', {
                type: 'error',
                message: msg,
            }, {root: true});
        }

    },

    async revokeRoomAdmin({ commit, dispatch, state, rootState }, payload) {

        try {

            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            const response = await axios.post(`/v2/chat/rooms/revoke-room-admin`, {
                RoomID: payload.RoomID,
                MemberID: payload.MemberID,
            }, config);

            // commit('SET_ROOM_ADMINS', { RoomID: payload.RoomID, Admins: response.data.Admins });

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Admin privileges revoked'
            }, {root: true});

        }
        catch (err) {

            let msg = "Something went wrong. Admin privileges weren't revoked.";

            if (err?.response?.data?.message) {
                msg = err?.response?.data?.message;
            }
            dispatch('notifications/addNotification', {
                type: 'error',
                message: msg,
            }, {root: true});
        }

    },

    async updateRoomName({ commit, dispatch, state, rootState }, payload) {

        try {

            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            let payloadAPI = {
                RoomID: payload.RoomID,
                RoomName: payload.RoomName,
            };

            // add new backend API for updating room name
            await axios.post(`/v2/chat/rooms/update-room-name`, payloadAPI, config);

            commit('SET_ROOM_NAME', payloadAPI);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Conversation name successfully updated'
            }, {root: true});

            // await dispatch('fetchRooms');

        }
        catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. Conversation name wasn't updated."
            }, {root: true});
        }

    },

    async updateRoomPicture({ commit, dispatch, state, rootState }, payload) {

        try {

            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            let newRoomPicture = payload.NewRoomPicture;
            let newRoomPictureName = `${newRoomPicture.name}.${newRoomPicture.ext}`;

            let payloadAPI = {
                RoomID: payload.RoomID,
                RoomPicture: newRoomPictureName
            };

            // upload new picture
            await dispatch('file/uploadFile', newRoomPicture, {root: true});

            // delete old picture
            if (payload.OldRoomPicture !== null && payload.OldRoomPicture !== undefined) {

                const delete_payload = {
                    folder_name: `chat/${payload.RoomID}`,
                    file_name: payload.OldRoomPicture
                }

                await dispatch('file/deleteFile', delete_payload, {root: true});

                let file_id = payload.OldRoomPicture.substring(0, payload.OldRoomPicture.lastIndexOf('.'));

                if (file_id) {

                    const delete_old_picture_from_resized_payload = {
                        folder_name: `chat/${payload.RoomID}`,
                        file_id: file_id
                    };

                    await dispatch('file/deleteResizedVersionsOfFile', delete_old_picture_from_resized_payload, {root: true});

                }
            }

            // add new backend API for updating room picture
            await axios.post(`/v2/chat/rooms/update-room-picture`, payloadAPI, config);

            commit('SET_ROOM_PICTURE', payloadAPI);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Group picture successfully updated'
            }, {root: true});

            // await dispatch('fetchRooms');

        }
        catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. Group picture wasn't updated."
            }, {root: true});
        }

    },

    async addReaction({ commit, dispatch, rootState, rootGetters }, payload) {

        const currentUserID = rootGetters['auth/getCurrentUserID'];

        commit('ADD_REACTION', {
            roomID: payload.roomID,
            messageID: payload.messageID,
            emoji: payload.emoji,
            userID: currentUserID
        });

        try {

            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization:
                    rootState.auth.bearer
                }
            };

            let payloadAPI = {
                emoji: payload.emoji,
            };

            await axios.post(
                `/v2/chat/rooms/${payload.roomID}/messages/${payload.messageID}/add-reaction`,
                payloadAPI,
                config
            );
        } catch (err) {
            commit('REMOVE_REACTION', {
                roomID: payload.roomID,
                messageID: payload.messageID,
                emoji: payload.emoji,
                userID: currentUserID
            });
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. Your reaction couldn't be added.",
            }, { root: true });
        }
    },

    async removeReaction({ commit, dispatch, rootState, rootGetters }, payload) {

        const currentUserID = rootGetters['auth/getCurrentUserID'];

        commit('REMOVE_REACTION', {
            roomID: payload.roomID,
            messageID: payload.messageID,
            emoji: payload.emoji,
            userID: currentUserID
        });

        try {

            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer
                }
            };

            let payloadAPI = {
                emoji: payload.emoji,
            };

            await axios.post(
                `/v2/chat/rooms/${payload.roomID}/messages/${payload.messageID}/remove-reaction`,
                payloadAPI,
                config
            );
        } catch (err) {
            commit('ADD_REACTION', {
                roomID: payload.roomID,
                messageID: payload.messageID,
                emoji: payload.emoji,
                userID: currentUserID
            });
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. Your reaction couldn't be removed.",
            }, { root: true });
        }
    },

    async getAttachmentUploadUrl({ dispatch, rootState }, { roomID, filename, contentType }) {

        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        const config = {
            headers: {
                Authorization: rootState.auth.bearer
            }
        };

        let payloadAPI = {
            filename: filename,
            contentType: contentType,
        }

        try {
            const response = await axios.post(
                `/v2/chat/rooms/${roomID}/attachments/upload-url`,
                payloadAPI,
                config
            );

            return response.data;
        }
        catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. The file couldn't be uploaded.",
            }, { root: true });
        }

    },

    async getAttachmentDownloadUrl({ state, commit, dispatch, rootState }, { roomID, fileID, filename, contentType, skipCache }) {

        const cached = state.AttachmentUrlCache[fileID];
        if (!skipCache && cached && Date.now() < cached.expiresAt) {
            return cached.url;
        }

        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        const config = {
            headers: {
                Authorization: rootState.auth.bearer
            }
        };

        try {
            const response = await axios.post(
                `/v2/chat/rooms/${roomID}/attachments/download-url`,
                { fileID, filename, contentType },
                config
            );

            const url = response.data.downloadUrl;
            commit('CACHE_ATTACHMENT_URL', { fileID, url });
            return url;
        }
        catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. The attachment couldn't be loaded.",
            }, { root: true });
        }

    },

    async deleteAttachment({ rootState }, { roomID, fileID }) {

        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        const config = {
            headers: {
                Authorization: rootState.auth.bearer
            }
        };

        try {
            await axios.post(
                `/v2/chat/rooms/${roomID}/attachments/delete`,
                { fileID },
                config
            );
        } catch (err) {
            // fire-and-forget — orphan is acceptable if the delete request fails
        }

    },

    async editMessage({ commit }, { roomID, messageID, content }) {
        commit('UPDATE_MESSAGE', { roomID, messageID, content, editedAt: new Date().toISOString() });
        websocketService.editMessage(roomID, messageID, content);
    },

    async deleteMessage({ commit }, { roomID, messageID }) {
        commit('REMOVE_MESSAGE', { roomID, messageID });
        websocketService.deleteMessage(roomID, messageID);
    },

    async deleteRoom({ commit, dispatch, state, rootState }, roomID) {

        try {

            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            await axios.delete(`/v2/chat/rooms/${roomID}`, config);

            // If this was the active room, close it first
            if (state.ActiveRoomID === roomID) {
                commit('SET_ACTIVE_ROOM', null);
            }

            // Remove from local rooms list without a full refetch
            commit('REMOVE_ROOM', roomID);

        }
        catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: "Something went wrong. Conversation wasn't deleted"
            }, {root: true});
        }


    },

};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};